import { z } from 'zod';
/**
 * A node is anything worth keeping a record of: body weight, resting heart
 * rate, a reading list, a family tree. Node *types* are declared in code
 * (see `catalog.ts`); a user then instantiates a type into their space, which
 * is what the `nodes` table stores.
 *
 * Adding a new thing to track should mean adding one entry to the catalog and
 * nothing else. Everything downstream — forms, charts, goals, the dashboard —
 * reads its behaviour from the definition.
 */
/** How a node's records are shaped, which decides how the UI renders it. */
export const nodeKindSchema = z.enum([
    /** One number per point in time. Charts and goals both apply. */
    'metric',
    /** A set of structured records, e.g. people in a family tree. */
    'collection',
    /** Long-form content and files, e.g. a research write-up. */
    'document',
]);
/** Which way "better" points, so progress can be phrased honestly. */
export const directionSchema = z.enum(['lower-is-better', 'higher-is-better', 'neutral']);
/** How several records inside one bucket (a day, a week) collapse to one number. */
export const aggregationSchema = z.enum(['last', 'first', 'mean', 'median', 'sum', 'min', 'max']);
export const unitSchema = z.object({
    /** Stored unit. Everything is persisted in this unit, always. */
    canonical: z.string(),
    /** Shown to the reader. May differ per user later (kg vs lb). */
    display: z.string(),
    /** Decimals to render. Storage keeps full precision. */
    precision: z.number().int().min(0).max(4).default(1),
});
export const metricSpecSchema = z.object({
    unit: unitSchema,
    /**
     * Deliberately absent: a metric has no direction.
     *
     * The catalog used to say body fat was 'lower-is-better' and muscle mass
     * 'higher-is-better', and a goal inherited that whether or not it agreed —
     * so somebody raising their body fat from 12% to 18% was told they were
     * going the wrong way at every step of doing exactly what they meant to.
     *
     * Which way is better is not a property of a measurement. It is a property
     * of what one person decided they wanted, and the only place that is ever
     * known is a goal they set themselves, where the baseline and the target
     * say it outright. `directionSchema` still exists for that; it just no
     * longer lives here.
     */
    aggregation: aggregationSchema.default('last'),
    /** Values outside this are almost certainly typos or a device glitch. */
    plausibleRange: z.object({ min: z.number(), max: z.number() }).optional(),
    /** Seeds the y-axis so a flat series doesn't look like a rollercoaster. */
    suggestedAxis: z.object({ min: z.number(), max: z.number() }).optional(),
});
/** Where a record came from. Manual entries are editable; synced ones are not. */
export const sourceSchema = z.enum(['manual', 'withings', 'import']);
//# sourceMappingURL=types.js.map